import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { trackCompleteRegistration } from '@/lib/metaPixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, already_verified, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link');
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-email?token=${token}`);
      const data = await response.json();

      if (response.ok) {
        if (data.already_verified) {
          setStatus('already_verified');
          setMessage('Your email is already verified');
        } else {
          setStatus('success');
          setMessage(data.message || 'Email verified successfully!');
          // Meta Pixel: email verification completes the registration
          // funnel step. Fires once per account — the `already_verified`
          // branch above intentionally skips this so a user bookmarking
          // the verify link doesn't re-fire the event on every revisit.
          trackCompleteRegistration({ method: 'email' });
        }
      } else {
        setStatus('error');
        setMessage(data.detail || 'Verification failed');
      }
    } catch (error) {
      setStatus('error');
      setMessage('An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-slate-200 shadow-lg">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-[#00247D] animate-spin mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                VERIFYING EMAIL
              </h2>
              <p className="text-slate-600">Please wait...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                EMAIL VERIFIED!
              </h2>
              <p className="text-slate-600 mb-6">{message}</p>
              <p className="text-slate-500 text-sm mb-6">
                Your account is now fully activated. You can access all features of Schedule Guru.
              </p>
              <Link to="/">
                <Button className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="continue-btn">
                  Continue to Schedule Guru
                </Button>
              </Link>
            </>
          )}

          {status === 'already_verified' && (
            <>
              <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
                <Mail className="w-12 h-12 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                ALREADY VERIFIED
              </h2>
              <p className="text-slate-600 mb-6">{message}</p>
              <Link to="/">
                <Button className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="continue-btn">
                  Continue to Schedule Guru
                </Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-12 h-12 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                VERIFICATION FAILED
              </h2>
              <p className="text-slate-600 mb-6">{message}</p>
              <p className="text-slate-500 text-sm mb-6">
                The verification link may be invalid or expired. Please try requesting a new verification email.
              </p>
              <div className="flex flex-col gap-3">
                <Link to="/login">
                  <Button className="w-full bg-[#00247D] hover:bg-[#001A5C]" data-testid="login-btn">
                    Go to Login
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="w-full">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
